import { Modal, Checkbox, Select } from 'antd';
import React from 'react';
import { observer } from 'mobx-react';
import ewtStore from '../EWTStore';
import { ewtModalClass } from '../theme';
import { LabeledInput, LabeledSelect } from './LabeledFormControls';

const getCourseTeeOptions = (course) => {
    if (!course || !course.holes) return [];
    const holeKeys = Object.keys(course.holes).sort(
        (a, b) => parseInt(a, 10) - parseInt(b, 10)
    );
    if (holeKeys.length === 0) return [];
    const firstHole = course.holes[holeKeys[0]];
    if (!firstHole || !firstHole.tees) return [];
    return Object.keys(firstHole.tees);
};

export const DataRoundEdit = observer((props) => {
    const [round, setRound] = React.useState(null);
    const [courses, setCourses] = React.useState([]);
    const [makeCurrentRound, setMakeCurrentRound] = React.useState(false);

    React.useEffect(() => {
        if (props.round) {
            let round = JSON.parse(JSON.stringify(props.round));
            setRound(round);
            setMakeCurrentRound(false);
        }
    }, [props.round]);

    React.useEffect(() => {
        if (!props.open) return;
        ewtStore.getAllDocs('courses').then((courseList) => {
            setCourses(courseList || []);
        });
    }, [props.open]);

    const selectedCourse = courses.find((course) => course.id === round?.courseID);
    const teeOptions = getCourseTeeOptions(selectedCourse);
    const isCurrentRound = !!(round?.id && ewtStore.currentRoundID === round.id);

    const currentRoundChanged = (e) => {
        if (round.id) {
            if (e.target.checked) {
                ewtStore.setCurrentRound(round.id);
            }
            return;
        }
        setMakeCurrentRound(e.target.checked);
    };

    const save = async () => {
        let roundToSave = JSON.parse(JSON.stringify(round));
        if (roundToSave.groups) {
            Object.keys(roundToSave.groups).forEach((groupID) => {
                delete roundToSave.groups[groupID].groupPlayers;
            });
        }
        delete roundToSave.id;
        if (round.id) {
            await ewtStore.updateOneDoc('rounds', round.id, roundToSave);
        } else {
            const newId = await ewtStore.getNextSequentialId('rounds');
            await ewtStore.setOneDoc('rounds', newId, roundToSave);
            if (makeCurrentRound) {
                await ewtStore.setCurrentRound(newId);
            }
        }
        props.hideModal();
    };

    return (
        <Modal
            className={ewtModalClass}
            title={round?.id ? 'Edit Round' : 'Add Round'}
            open={props.open}
            onOk={save}
            onCancel={props.hideModal}
            okText="Save"
            cancelText="Cancel"
        >
            {round && (
                <div className="flexColumn ewtLabeledForm" style={{ gap: 8 }}>
                    <LabeledInput
                        label="Name"
                        value={round.name}
                        onChange={(e) => setRound({ ...round, name: e.target.value })}
                    />
                    <LabeledInput
                        label="Date"
                        value={round.date}
                        onChange={(e) => setRound({ ...round, date: e.target.value })}
                    />
                    <LabeledInput
                        label="Round #"
                        value={round.roundNumber}
                        onChange={(e) =>
                            setRound({ ...round, roundNumber: e.target.value })
                        }
                    />
                    <LabeledInput
                        label="Short Name"
                        value={round.shortName}
                        onChange={(e) =>
                            setRound({ ...round, shortName: e.target.value })
                        }
                    />
                    <LabeledInput
                        label="Starting Hole"
                        value={round.startingHole || '1'}
                        onChange={(e) =>
                            setRound({ ...round, startingHole: e.target.value })
                        }
                    />
                    <LabeledSelect
                        label="Course"
                        value={round.courseID || undefined}
                        onChange={(courseID) => {
                            const course = courses.find((c) => c.id === courseID);
                            const options = getCourseTeeOptions(course);
                            setRound({
                                ...round,
                                courseID,
                                tees: options.includes(round.tees)
                                    ? round.tees
                                    : options[0] || '',
                            });
                        }}
                        placeholder="Select course"
                    >
                        {courses.map((course) => (
                            <Select.Option key={course.id} value={course.id}>
                                {course.name}
                            </Select.Option>
                        ))}
                    </LabeledSelect>
                    <LabeledSelect
                        label="Tees"
                        value={round.tees || undefined}
                        onChange={(tees) => setRound({ ...round, tees })}
                        placeholder="Select tees"
                        disabled={!selectedCourse || teeOptions.length === 0}
                    >
                        {teeOptions.map((tee) => (
                            <Select.Option key={tee} value={tee}>
                                {tee}
                            </Select.Option>
                        ))}
                    </LabeledSelect>
                    <div className="flexColumn">
                        <div>
                            <Checkbox
                                checked={round.id ? isCurrentRound : makeCurrentRound}
                                disabled={isCurrentRound}
                                onChange={currentRoundChanged}
                            >
                                Current Round
                            </Checkbox>
                        </div>
                        <div>
                            <Checkbox
                                checked={round.open}
                                onChange={(e) =>
                                    setRound({ ...round, open: e.target.checked })
                                }
                            >
                                Open for Scoring
                            </Checkbox>
                        </div>
                        <div>
                            <Checkbox
                                checked={round.fixedPairings}
                                onChange={(e) =>
                                    setRound({ ...round, fixedPairings: e.target.checked })
                                }
                            >
                                Fixed Pairings
                            </Checkbox>
                        </div>
                        <div>
                            <Checkbox
                                checked={round.finalized}
                                onChange={(e) =>
                                    setRound({ ...round, finalized: e.target.checked })
                                }
                            >
                                Finalized
                            </Checkbox>
                        </div>
                        <div>
                            <Checkbox
                                checked={!!round.practice}
                                onChange={(e) =>
                                    setRound({ ...round, practice: e.target.checked })
                                }
                            >
                                Practice Round
                            </Checkbox>
                        </div>
                    </div>
                </div>
            )}
        </Modal>
    );
});
